import { z } from "zod";

import {
  getBirthYearFromEmiratesId,
  getYearFromDob,
} from "@/D2C/features/travel-quote/utils/travelAdditionalInfo";
import { PHONE_LENGTHS } from "@/shared/config/contact";
import { getHomeCountryLabel } from "@/shared/config/localisation";
import { getNationalIdValidationMessage, isValidNationalId } from "@/shared/utils/nationalId";
import { emailSchema } from "./emailSchema";
import { createPersonNameSchema } from "./nameSchemas";

const parseJourneyDate = (value: string): Date | null => {
  if (!value || typeof value !== "string") return null;

  const normalized = value.trim();
  if (!normalized) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const parsed = new Date(`${normalized}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const dotFormat = normalized.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dotFormat) {
    const [, day, month, year] = dotFormat;
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export const travelDetailsSchema = z
  .object({
    coverage: z.string().min(1, "Coverage is required"),
    fromCountry: z.preprocess(
      (value) => (typeof value === "string" ? value.trim() : value),
      z.string().min(1, "Country is required")
    ),
    destination: z.preprocess(
      (value) => (typeof value === "string" ? value.trim() : value),
      z.string().min(1, "Country is required")
    ),
    planType: z.union([z.literal("single"), z.literal("annual")]),
    startDate: z.preprocess(
      (value) => (typeof value === "string" ? value.trim() : value),
      z
        .string()
        .min(1, "Start date is required")
        .refine(
          (value) => {
            const date = parseJourneyDate(value);

            if (!date) {
              return false;
            }

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            date.setHours(0, 0, 0, 0);

            return date >= today;
          },
          {
            message: "Start date must be today or a future date",
          }
        )
    ),
    endDate: z.preprocess(
      (value) => (typeof value === "string" ? value.trim() : value),
      z
        .string()
        .min(1, "End date is required")
        .refine((value) => Boolean(parseJourneyDate(value)), {
          message: "Please enter a valid end date",
        })
    ),
    coverType: z.union([z.literal("individual"), z.literal("group"), z.literal("family")]),
    travellers: z
      .array(
        z.object({
          name: z.string().optional(),
          dob: z
            .string()
            .min(1, "Date of birth is required")
            .refine((dob) => {
              if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
                return false;
              }

              const birthDate = new Date(dob);
              if (Number.isNaN(birthDate.getTime())) {
                return false;
              }

              const today = new Date();
              const age = today.getFullYear() - birthDate.getFullYear();
              const monthDiff = today.getMonth() - birthDate.getMonth();
              const dayDiff = today.getDate() - birthDate.getDate();
              const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

              return actualAge >= 0 && actualAge <= 100;
            }, "Please enter a valid date of birth"),
        })
      )
      .min(1)
      .max(6),
  })
  .superRefine((data, ctx) => {
    const primaryDob = parseJourneyDate(data.travellers[0]?.dob);
    if (primaryDob) {
      const today = new Date();
      const birthdayPending =
        today.getMonth() < primaryDob.getMonth() ||
        (today.getMonth() === primaryDob.getMonth() && today.getDate() < primaryDob.getDate());
      const age = today.getFullYear() - primaryDob.getFullYear() - (birthdayPending ? 1 : 0);

      if (age < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Primary traveller must be at least 18 years old",
          path: ["travellers", 0, "dob"],
        });
      }
    }

    data.travellers.forEach((traveller, index) => {
      const name = typeof traveller.name === "string" ? traveller.name.trim() : "";
      const label = index === 0 ? "Primary traveller name" : `Traveller ${index + 1} name`;

      if (!name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} is required`,
          path: ["travellers", index, "name"],
        });
        return;
      }

      if (name.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} must be at least 2 characters`,
          path: ["travellers", index, "name"],
        });
        return;
      }

      if (name.length > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} cannot exceed 50 characters`,
          path: ["travellers", index, "name"],
        });
        return;
      }

      if (!/^[A-Za-z ]+$/.test(name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} must contain only letters`,
          path: ["travellers", index, "name"],
        });
        return;
      }

      if (/\s{2,}/.test(name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} cannot contain consecutive spaces`,
          path: ["travellers", index, "name"],
        });
        return;
      }

      if (/(.)\1{4,}/i.test(name.replace(/\s/g, ""))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label} contains too many repeated characters`,
          path: ["travellers", index, "name"],
        });
      }
    });
  })
  .superRefine((data, ctx) => {
    if (data.fromCountry && data.destination && data.fromCountry === data.destination) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "From and To countries cannot be the same",
        path: ["destination"],
      });
    }

    const startDate = parseJourneyDate(data.startDate);
    const endDate = parseJourneyDate(data.endDate);

    if (startDate && endDate) {
      if (endDate < startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date cannot be before start date",
          path: ["endDate"],
        });
      }

      if (data.planType === "single") {
        const maxEndDate = new Date(startDate);
        maxEndDate.setDate(maxEndDate.getDate() + 179);

        if (endDate > maxEndDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Enter a travel date within 179 days of your start date",
            path: ["endDate"],
          });
        }
      }
    }
  });

export const personalDetailsSchema = z.object({
  firstName: createPersonNameSchema("First name"),
  lastName: createPersonNameSchema("Last name"),
  mobileNumber: z
    .object({
      countryCode: z.string().min(1, "Country code is required"),
      number: z.preprocess(
        (value) => (typeof value === "string" ? value.trim() : value),
        z
          .string()
          .min(1, "Enter a valid mobile number")
          .regex(/^\d+$/, "Please enter only digits. Eg. 501234567")
      ),
    })
    .superRefine((value, ctx) => {
      const number = typeof value.number === "string" ? value.number : "";
      const requiredLength = PHONE_LENGTHS[value.countryCode];

      if (requiredLength && number.length !== requiredLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Enter a valid ${requiredLength}-digit mobile number`,
          path: ["number"],
        });
      }

      if (value.countryCode === "971") {
        const allowedPrefixes = ["50", "52", "54", "55", "56", "58"];

        if (value.number.length !== 9) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter only 9 digits Eg. 501234567.",
            path: ["number"],
          });
          return;
        }

        if (!allowedPrefixes.some((prefix) => value.number.startsWith(prefix))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Mobile number must start with 50, 52, 54, 55, 56, or 58.",
            path: ["number"],
          });
        }

        return;
      }
    }),
  email: emailSchema,
  partnerCode: z
    .string()
    .optional()
    .refine((value) => !value || /^[A-Za-z0-9]{8}$/.test(value), {
      message: "Partner Code must be 8 alphanumeric characters",
    }),
  friendsAndFamilyContact: z
    .string()
    .optional()
    .refine((value) => !value || /^\d{9}$/.test(value), {
      message: "Friends & Family Contact must be a 9 digit number",
    }),
  marketingConsent: z
    .preprocess((value) => (typeof value === "string" ? value.trim() : value), z.string())
    .refine((value) => value === "yes" || value === "no", {
      message: "Please select Yes or No",
    })
    .transform((value) => value as "yes" | "no"),
});

const travelAdditionalInfoBaseSchema = z.object({
  gender: z
    .string()
    .min(1, "Please select gender")
    .refine((value) => ["Male", "Female"].includes(value), {
      message: "Invalid gender",
    }),
  nationality: z.string().min(1, "Nationality is required"),
  cityOfResidence: z
    .string()
    .min(1, "City of residence is required")
    .regex(/^[A-Za-z\s.-]+$/, "Enter a valid city name"),
  address: z
    .string()
    .min(1, "Address is required")
    .max(200, "Address must not exceed 200 characters."),
  emiratesId: z.string(),
  passportNumber: z
    .string()
    .regex(
      /^[A-Z]\d{7}$/,
      "Passport Number must be 8 characters (1 capital letter followed by 7 digits)."
    ),
  sourceOfFund: z.string().min(1, "Source of fund is required"),
  beneficiaryName: createPersonNameSchema("Beneficiary name"),
  beneficiaryRelation: z
    .string()
    .min(1, "Beneficiary relation is required")
    .refine((value) => ["Legal Heir", "Other"].includes(value), {
      message: "Invalid beneficiary relation",
    }),
  uaeResidentConfirm: z.boolean().refine((value) => value === true, {
    message: `Please confirm ${getHomeCountryLabel()} residency`,
  }),
});

const additionalTravellerInfoSchema = z.object({
  gender: z
    .string()
    .min(1, "Please select gender")
    .refine((value) => ["Male", "Female"].includes(value), {
      message: "Invalid gender",
    }),
  nationality: z.string().min(1, "Nationality is required"),
  passportNumber: z
    .string()
    .regex(
      /^[A-Z]\d{7}$/,
      "Passport Number must be 8 characters (1 capital letter followed by 7 digits)."
    ),
});

export function createTravelAdditionalInfoSchema(params?: {
  requireEmiratesId?: boolean;
  travellerDob?: string;
}) {
  const requireEmiratesId = params?.requireEmiratesId ?? true;
  const travellerDob = params?.travellerDob ?? "";

  return travelAdditionalInfoBaseSchema
    .extend({
      travellers: z.array(additionalTravellerInfoSchema).optional(),
    })
    .superRefine((data, ctx) => {
      const validateEmiratesId = (
        emiratesId: string,
        dob: string,
        path: Array<string | number>
      ) => {
        if (requireEmiratesId && !emiratesId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: getNationalIdValidationMessage(),
            path,
          });
          return;
        }

        if (emiratesId && !isValidNationalId(emiratesId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: getNationalIdValidationMessage(),
            path,
          });
        }

        const emiratesBirthYear = emiratesId ? getBirthYearFromEmiratesId(emiratesId) : null;
        const travellerBirthYear = getYearFromDob(dob);

        if (
          requireEmiratesId &&
          emiratesId &&
          emiratesBirthYear &&
          travellerBirthYear &&
          emiratesBirthYear !== travellerBirthYear
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "The entered Date of Birth year does not match the ID document",
            path,
          });
        }
      };

      validateEmiratesId(data.emiratesId, travellerDob, ["emiratesId"]);
    });
}

export const travelAdditionalInfoSchema = createTravelAdditionalInfoSchema();
